import classNames from 'classnames';
import React, {Component, Children, cloneElement} from 'react';
import PropTypes from 'prop-types';
import injectSheet from 'react-jss';

import {important, rem} from '-/styles/utils';
import {screenSize, detectIE} from '-/util/util';
import Fieldset from '-/components/fields/Fieldset';

const USE_LABEL_WIDTH_THRESHOLD = 500;

const controlStyles = theme => ({
    root: {
        '&$labelWrap': {
            '& > :first-child': {
                borderRight: 'none',
                borderLeft: '1px solid gainsboro',
                borderBottomRightRadius: 0,
                borderTopRightRadius: 0,
                flex: 'none'
            },

            '&:not($labelAbove)': {
                '& > :last-child': {
                    flex: 1,
                    borderLeft: '1px solid gainsboro',
                    borderRight: '1px solid gainsboro',
                    borderBottomLeftRadius: 0,
                    borderTopLeftRadius: 0
                },
                '& .symbol-input': {
                    borderLeft: 'none',
                    borderRight: 'none'
                }
            }
        },
        '&:first-of-type': {
            '& > :last-child, & > :first-child': {
                borderTop: '1px solid #d6d6d6',
                borderTopLeftRadius: 5,
                borderTopRightRadius: 5
            }
        },
        '&:last-of-type': {
            '& > :last-child, & > :first-child': {
                borderBottom: '1px solid #d6d6d6',
                borderBottomLeftRadius: 5,
                borderBottomRightRadius: 5
            }
        },
        '&:not(:last-of-type)': {
            '> :last-child, & > :first-child': {
                borderBottom: '1px solid #d6d6d6'
            }
        }
    },
    labelWrap: {
        display: 'flex',
        minHeight: 47,
        '&:not($labelAbove)': {
            borderBottomLeftRadius: 0,
            borderTopLeftRadius: 0,

            '& .pay-rate.inline': {
                border: '0px !important',

                '& > :first-child': {
                    borderBottom: '1px solid #d6d6d6'
                },

                '& > :last-child': {
                    borderLeft: 0,
                    borderBottomRightRadius: 5,
                    borderBottom: '1px solid #d6d6d6'
                }
            }
        },
        '&$labelAbove': {
            '& > :first-child': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0
            },

            '& > :last-child': {
                borderTop: '0',
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0
            },
            '& .pay-rate.inline': {
                border: '0px !important',

                '& > :first-child': {
                    borderBottomLeftRadius: 5,
                    borderLeft: '1px solid #d6d6d6',
                    borderBottom: '1px solid #d6d6d6'
                },

                '& > :last-child': {
                    borderLeft: '0',
                    borderBottomRightRadius: 5,
                    borderRight: '1px solid #d6d6d6',
                    borderBottom: '1px solid #d6d6d6'
                }
            }
        },
        '&:last-of-type': {
            '&$labelAbove': {
                '& .geosuggest': {
                    border: '0px !important',

                    '& .geosuggest__input-wrapper input': {
                        borderLeft: '1px solid #d6d6d6',
                        borderBottomLeftRadius: 5,
                        borderBottomRightRadius: 5,
                        borderRight: '1px solid #d6d6d6',
                        borderBottom: '1px solid #d6d6d6'
                    }
                }
            }
        },
        '& .pay-rate.inline': {
            '& > *': {
                float: 'left',
                display: 'inline-block',
                width: '50% !important',
                margin: '0 !important',
                '&.select span': {
                    overflow: 'unset !important'
                }
            }
        }
    },
    label: {
        padding: `${rem(15)} ${rem(20)} 0`,
        fontSize: rem(14),
        fontWeight: 'bold',
        color: theme.colors.strong,
        opacity: .8
    },
    labelAbove: {
        flexDirection: 'column',
        '& > *': {
            width: '100%',
            display: 'block',
            minHeight: rem(47),
            '&:first-child': {
                borderRight: important('1px solid gainsboro')
            },
            '&:last-child': {
                '&:focus': {
                    borderTop: important('1px solid #4a90e2'),
                    borderBottom: important('1px solid #4a90e2')
                }
            }
        },
        '&:first-of-type:not(:last-of-type)': {
            '& > :first-child': {
                borderTop: '1px solid #d6d6d6',
                borderTopLeftRadius: 5,
                borderTopRightRadius: 5
            },
            '& > :last-child': {
                borderTop: important('0px solid #d6d6d6'),
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0
            }
        },
        '&:last-of-type:not(:first-of-type)': {
            '& > :first-child': {
                borderBottom: '1px solid #d6d6d6',
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0
            },
            '& > :last-child': {
                borderBottom: '1px solid #d6d6d6',
                borderBottomLeftRadius: 5,
                borderBottomRightRadius: 5
            }
        }
    }
});

class ControlCls extends Component {
    render() {
        const {children} = this.props;
        const [child] = Children.toArray(children);
        if (!child) return null;

        const {label} = child.props;
        const {labelWidth, classes = {}} = this.props;
        const labelsAbove = detectIE() || screenSize().width < USE_LABEL_WIDTH_THRESHOLD;
        const className = classNames(classes.root, {
            [classes.labelAbove]: labelsAbove,
            [classes.labelWrap]: label,
            'control': true
        }, this.props.className);

        let labelStyle = {};
        if (!labelsAbove && labelWidth) {
            labelStyle = {width: labelWidth};
        }

        return (
            <div className={className}>
                {label ? <label className={classes.label} style={labelStyle}>{label}</label> : null}
                {child}
            </div>
        );
    }
}

export class ControlGroup extends Component {
    static propTypes = {
        labelWidth: PropTypes.number,
        className: PropTypes.string
    };

    static defaultProps = {
        labelWidth: null
    };

    constructor(props) {
        super(props);

        this.state = {
            children: []
        };
    }

    componentDidMount() {
        this.updateLabelWidths(this.props);
    }

    componentWillReceiveProps(nextProps) {
        this.updateLabelWidths(nextProps);
    }

    updateLabelWidths(props) {
        let children = Children.toArray(props.children);
        children = children.map(child => {
            if (child.type === Control) {
                return cloneElement(child, {
                    labelWidth: props.labelWidth
                });
            }
            return child;
        });
        this.setState({children});
    }

    render() {
        let fieldsetProps = {...this.props};

        delete fieldsetProps.labelWidth;
        delete fieldsetProps.theme;
        delete fieldsetProps.classes;

        return (
            <Fieldset type="control" {...fieldsetProps}>
                {this.state.children}
            </Fieldset>
        );
    }
}

export const Control = injectSheet(controlStyles)(ControlCls);
